import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Dialog, List, ListItem, Select, TextField, useSnackbar } from '@meshble/molo';
import { api, must, mustOk } from '../lib/api';
import { quandoRelativo } from '../lib/attivita';
import { useMe, usePermessiSu } from '../session';
import { Anello } from '../brand';
import { localeIntl, t } from '../lib/lingua';

/**
 * «Accesso SSH»: dare a una persona dell'organizzazione un accesso al
 * server per qualche ora. L'hub firma un certificato OpenSSH con il suo
 * nome e la scadenza dentro; il server lo accetta finché vale, poi no. Il
 * firewall non si tocca: si entra passando dall'hub (`-J salto@hub`).
 *
 * Il certificato si vede una volta sola, qui: non è un segreto — vale solo
 * con la chiave privata della persona — ma non si conserva da nessuna parte.
 */
export function AccessoSSH({ slug, serverId, nomeServer }: { slug: string; serverId: string; nomeServer: string }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const amministra = usePermessiSu(slug, { server: serverId })('server.ssh');
  const [apri, setApri] = useState(false);

  const accessi = useQuery({
    queryKey: ['ssh-accesses', slug, serverId],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/servers/{id}/ssh-accesses', { params: { path: { slug, id: serverId } } })),
    enabled: !!amministra,
    refetchInterval: 60_000,
  });
  const revoca = useMutation({
    mutationFn: (accessId: string) =>
      mustOk(api.DELETE('/api/v1/orgs/{slug}/servers/{id}/ssh-accesses/{accessId}', { params: { path: { slug, id: serverId, accessId } } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ssh-accesses', slug, serverId] });
      avvisa(t('Accesso revocato: la chiave non entra più'));
    },
  });

  if (!amministra) return null;
  const vivi = accessi.data?.accesses ?? [];

  return (
    <div className="md-stack">
      <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
        {t('Il server accetta SSH solo dall’hub. Per entrarci, una persona riceve un certificato con il suo nome e una scadenza di qualche ora, e passa dall’hub. Ogni ingresso resta nel log del server con quel nome, e ogni concessione nel registro.')}
      </p>
      {accessi.error && <Banner tone="error" title={t('Non riesco a leggere gli accessi')}>{accessi.error.message}</Banner>}
      {revoca.error && <Banner tone="error" title={t('Revoca non applicata')}>{(revoca.error as Error).message}</Banner>}
      {vivi.length > 0 && (
        <List>
          {vivi.map((a) => (
            <ListItem
              key={a.id}
              headline={a.name ? `${a.name} (${a.email})` : a.email}
              supporting={[
                t('fino a {quando}', { quando: new Date(a.validUntil).toLocaleString(localeIntl(), { dateStyle: 'short', timeStyle: 'short' }) }),
                t('concesso {quando}', { quando: quandoRelativo(a.createdAt) }),
                ...(a.reason ? [a.reason] : []),
                t('n. {seriale}', { seriale: a.serial }),
              ].join(' · ')}
              trailing={
                <Button variant="text" disabled={revoca.isPending} onClick={() => revoca.mutate(a.id)}>{t('Revoca')}</Button>
              }
            />
          ))}
        </List>
      )}
      {accessi.isSuccess && vivi.length === 0 && (
        <p className="md-body-medium" style={{ margin: 0 }}>{t('Nessun accesso in corso.')}</p>
      )}
      <div className="md-stack__inline">
        <Button variant="tonal" onClick={() => setApri(true)}>{t('Dai un accesso')}</Button>
      </div>
      <Dialog
        open={apri}
        onOpenChange={(v) => !v && setApri(false)}
        title={t('Accesso SSH a {server}', { server: nomeServer })}
        description={t('A chi, per quanto, perché. Il certificato compare qui una volta sola.')}
        actions={<Button variant="text" onClick={() => setApri(false)}>{t('Chiudi')}</Button>}
      >
        {apri && <NuovoAccesso slug={slug} serverId={serverId} nomeServer={nomeServer} />}
      </Dialog>
    </div>
  );
}

function NuovoAccesso({ slug, serverId, nomeServer }: { slug: string; serverId: string; nomeServer: string }) {
  const me = useMe();
  const qc = useQueryClient();
  const membri = useQuery({
    queryKey: ['members', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/members', { params: { path: { slug } } })),
  });
  const [userId, setUserId] = useState(me.data?.id ?? '');
  // La propria chiave, se registrata, è già scritta: nel caso più comune
  // chi concede l'accesso è chi entra.
  const [chiave, setChiave] = useState(me.data?.sshPublicKey ?? '');
  const [ore, setOre] = useState('8');
  const [motivo, setMotivo] = useState('');
  const perMe = userId === me.data?.id;
  // Le durate al render e non a livello di modulo: le etichette seguono la lingua.
  const durate = [
    { value: '1', label: t('1 ora') },
    { value: '4', label: t('4 ore') },
    { value: '8', label: t('8 ore — una giornata di lavoro') },
    { value: '24', label: t('24 ore, il massimo') },
  ];
  const chiaveOk = chiave.trim() === '' || /^(ssh-ed25519|ssh-rsa|ecdsa-sha2-nistp(256|384|521)|sk-ssh-ed25519@openssh\.com|sk-ecdsa-sha2-nistp256@openssh\.com) [A-Za-z0-9+/=]+( .*)?$/.test(chiave.trim());

  const concedi = useMutation({
    mutationFn: () =>
      must(
        api.POST('/api/v1/orgs/{slug}/servers/{id}/ssh-accesses', {
          params: { path: { slug, id: serverId } },
          body: {
            userId, hours: Number(ore),
            ...(chiave.trim() ? { publicKey: chiave.trim() } : {}),
            ...(motivo.trim() ? { reason: motivo.trim() } : {}),
          },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ssh-accesses', slug, serverId] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  if (concedi.data) {
    const c = concedi.data;
    const file = `molo-${nomeServer}-cert.pub`;
    const scarica = () => {
      const url = URL.createObjectURL(new Blob([c.certificate + '\n'], { type: 'text/plain' }));
      const a = Object.assign(document.createElement('a'), { href: url, download: file });
      a.click();
      URL.revokeObjectURL(url);
    };
    return (
      <div className="md-stack">
        <Banner tone="success" title={t('Accesso per {chi} fino alle {ora} di {giorno}', {
          chi: c.name || c.email,
          ora: new Date(c.validUntil).toLocaleTimeString(localeIntl(), { timeStyle: 'short' }),
          giorno: new Date(c.validUntil).toLocaleDateString(localeIntl()),
        })}>
          {t('Il certificato vale con la chiave privata di questa persona, e solo per {server}. Poi scade da solo.', { server: nomeServer })}
        </Banner>
        <p className="md-body-medium" style={{ margin: 0 }}>
          {t('1. Salva il certificato accanto alla chiave privata, con questo nome: {file}', { file: <code className="md-mono">{file}</code> })}
        </p>
        <div className="md-stack__inline">
          <Button variant="tonal" onClick={scarica}>{t('Scarica il certificato')}</Button>
          <Button variant="text" onClick={() => navigator.clipboard.writeText(c.certificate)}>{t('Copia')}</Button>
        </div>
        <p className="md-body-medium" style={{ margin: 0 }}>{t('2. Entra passando dall’hub:')}</p>
        <pre className="md-code" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{c.command}</pre>
        <div className="md-stack__inline">
          <Button variant="text" onClick={() => navigator.clipboard.writeText(c.command)}>{t('Copia il comando')}</Button>
        </div>
        <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
          {t('Se la chiave privata non è quella predefinita, aggiungi {opzione}.', { opzione: <code className="md-mono">{t('-i percorso/della/chiave')}</code> })}{' '}
          {t('Nel log del server questo ingresso compare come {utente}.', { utente: <code className="md-mono">molo:{c.email}:{c.serial}</code> })}
        </p>
      </div>
    );
  }

  return (
    <div className="md-stack">
      {concedi.error && <Banner tone="error" title={t('Accesso non concesso')}>{(concedi.error as Error).message}</Banner>}
      <Select
        label={t('A chi')}
        value={userId}
        options={(membri.data?.members ?? []).map((m) => ({ value: m.userId, label: m.name ? `${m.name} (${m.email})` : m.email }))}
        onChange={(e) => { setUserId(e.target.value); setChiave(e.target.value === me.data?.id ? (me.data?.sshPublicKey ?? '') : ''); }}
      />
      <div className={`md-field${chiaveOk ? '' : ' md-field--error'}`}>
        <label htmlFor="chiave-ssh">{t('Chiave pubblica SSH')}</label>
        <textarea
          id="chiave-ssh"
          rows={3}
          spellCheck={false}
          placeholder={t('ssh-ed25519 AAAA… nome@computer')}
          value={chiave}
          onChange={(e) => setChiave(e.target.value)}
        />
        <span className="md-field__help">
          {!chiaveOk
            ? t('Non è una riga di chiave pubblica: è il contenuto di ~/.ssh/id_ed25519.pub, tutto su una riga.')
            : perMe
              ? t('Il contenuto di ~/.ssh/id_ed25519.pub. Resta registrata: la prossima volta è già qui.')
              : t('Chiedigliela e incollala qui; se è vuota uso quella che ha già registrato. Mai la chiave privata.')}
        </span>
      </div>
      <Select label={t('Per quanto')} value={ore} options={durate} onChange={(e) => setOre(e.target.value)} />
      <TextField
        label={t('Perché')}
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        placeholder={t('es. debug del modulo vendite per il ticket 412')}
        help={t('Finisce nel registro accanto al nome: fra un mese è quello che spiega questo accesso.')}
      />
      <div className="md-stack__inline" style={{ justifyContent: 'flex-end' }}>
        <Button
          variant="filled"
          icon={concedi.isPending ? <Anello /> : undefined}
          disabled={!userId || !chiaveOk || concedi.isPending}
          onClick={() => concedi.mutate()}
        >
          {concedi.isPending ? t('Firmo…') : t('Dai l’accesso')}
        </Button>
      </div>
    </div>
  );
}
